import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { API_BASE } from '../config';
import { clearClientStorageOnLogout } from '../utils/clear-client-storage';

interface User {
  id: string;
  email: string;
  admin: boolean;
  tier: 'free' | 'pro' | 'editorial';
  preferredLanguageCode: string;
  createdAt: string;
  featureFlags?: string[];
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface SignupCredentials {
  email: string;
  password: string;
  invitationCode?: string;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  loading: boolean;
  masterKey: string | null;
  originalAdminUser: User | null;
  signup: (credentials: SignupCredentials) => Promise<void>;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonate: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [originalAdminUser, setOriginalAdminUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('originalAdminUser');
    return stored ? JSON.parse(stored) : null;
  });
  const [masterKey, setMasterKey] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('master_key');
  });

  const updateSentryUser = useCallback((currentUser: User | null, adminUser: User | null) => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      if (currentUser) {
        Sentry.setUser({
          id: currentUser.id,
          email: currentUser.email,
        });
        if (adminUser) {
          Sentry.setTag('impersonation', 'true');
          Sentry.setTag('impersonated_user_id', currentUser.id);
          Sentry.setTag('impersonated_user_email', currentUser.email);
          Sentry.setTag('admin_user_id', adminUser.id);
          Sentry.setTag('admin_user_email', adminUser.email);
        } else {
          Sentry.setTag('impersonation', 'false');
          Sentry.setTag('impersonated_user_id', undefined);
          Sentry.setTag('impersonated_user_email', undefined);
          Sentry.setTag('admin_user_id', undefined);
          Sentry.setTag('admin_user_email', undefined);
        }
      } else {
        Sentry.setUser(null);
        Sentry.setTag('impersonation', undefined);
        Sentry.setTag('impersonated_user_id', undefined);
        Sentry.setTag('impersonated_user_email', undefined);
        Sentry.setTag('admin_user_id', undefined);
        Sentry.setTag('admin_user_email', undefined);
      }
    }
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlMasterKey = params.get('master_key');
      
      if (urlMasterKey) {
        setMasterKey(urlMasterKey);
        const masterUser = {
          id: '__master__',
          email: 'master@test.local',
          admin: true,
          tier: 'free' as const,
          preferredLanguageCode: 'en',
          createdAt: new Date().toISOString(),
          featureFlags: [] as string[],
        };
        setUser(masterUser);
        updateSentryUser(masterUser, null);
        setLoading(false);
        return;
      }

      const storedOriginalAdminUser = localStorage.getItem('originalAdminUser');
      const storedOriginalAdminUserParsed = storedOriginalAdminUser ? JSON.parse(storedOriginalAdminUser) : null;

      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
        try {
          const response = await fetch(`${API_BASE}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            localStorage.setItem('user', JSON.stringify(data.user));
            if (storedOriginalAdminUserParsed) {
              setOriginalAdminUser(storedOriginalAdminUserParsed);
            }
            updateSentryUser(data.user, storedOriginalAdminUserParsed);
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('originalAdminUser');
            setToken(null);
            setUser(null);
            setOriginalAdminUser(null);
            updateSentryUser(null, null);
          }
        } catch (error) {
          console.error('Error fetching user:', error);
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              setUser(parsedUser);
              if (storedOriginalAdminUserParsed) {
                setOriginalAdminUser(storedOriginalAdminUserParsed);
              }
              updateSentryUser(parsedUser, storedOriginalAdminUserParsed);
            } catch {
              localStorage.removeItem('user');
              localStorage.removeItem('originalAdminUser');
              setUser(null);
              setOriginalAdminUser(null);
              updateSentryUser(null, null);
            }
          } else {
            updateSentryUser(null, null);
          }
        }
      } else {
        updateSentryUser(null, null);
      }
      setLoading(false);
    };
    fetchUser();
  }, [updateSentryUser]);

  const signup = useCallback(async (credentials: SignupCredentials): Promise<void> => {
    const response = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Signup failed');
    }

    const data = await response.json();
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    updateSentryUser(data.user, null);
  }, [updateSentryUser]);

  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const err = new Error(errorData.error || 'Login failed') as Error & { errorCode?: string };
      err.errorCode = errorData.errorCode;
      throw err;
    }

    const data = await response.json();
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    updateSentryUser(data.user, null);
  }, [updateSentryUser]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setOriginalAdminUser(null);
    clearClientStorageOnLogout();
    updateSentryUser(null, null);
  }, [updateSentryUser]);

  const impersonate = useCallback(async (userId: string): Promise<void> => {
    if (!user || !user.admin || !token) {
      throw new Error('Only admins can impersonate users');
    }

    localStorage.setItem('originalToken', token);

    const response = await fetch(`${API_BASE}/api/users/${userId}/impersonate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      localStorage.removeItem('originalToken');
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to impersonate user');
    }

    const data = await response.json();
    setOriginalAdminUser(user);
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('originalAdminUser', JSON.stringify(user));
    updateSentryUser(data.user, user);
  }, [user, token, updateSentryUser]);

  const stopImpersonate = useCallback(async () => {
    if (!originalAdminUser) {
      return;
    }

    if (token) {
      try {
        await fetch(`${API_BASE}/api/impersonation-actions/stop`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('Error logging impersonation stop:', error);
      }
    }

    const originalToken = localStorage.getItem('originalToken');
    if (!originalToken) {
      setToken(null);
      setUser(null);
      setOriginalAdminUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('originalAdminUser');
      updateSentryUser(null, null);
      return;
    }

    setToken(originalToken);
    setUser(originalAdminUser);
    setOriginalAdminUser(null);
    localStorage.setItem('token', originalToken);
    localStorage.setItem('user', JSON.stringify(originalAdminUser));
    localStorage.removeItem('originalAdminUser');
    localStorage.removeItem('originalToken');
    updateSentryUser(originalAdminUser, null);
  }, [originalAdminUser, token, updateSentryUser]);

  return (
    <AuthContext.Provider value={{ token, user, loading, masterKey, originalAdminUser, signup, login, logout, impersonate, stopImpersonate }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
